/**
 * Privacy Policy content.
 */
export const PRIVACY_MARKDOWN = `# Privacy Policy

**Last updated: 26 February 2026**

**Void Inc.** ("we", "us", "our") is headquartered in Brisbane, Queensland, Australia. We are committed to protecting your privacy in accordance with the *Privacy Act 1988* (Cth) and the Australian Privacy Principles (APPs). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our calling and voice services platform (the "Service").

---

## 1. What personal information we collect

We may collect:

- **Account information:** name, email address, phone number, password (stored in hashed form), and profile details you provide when you register or update your account.
- **Call and voice data:** call recordings, transcripts, and related metadata when you use our call features.
- **Payment information:** billing details and payment history. Card payments are processed by Stripe; we do not store your full card number on our systems.
- **Usage and technical data:** how you use the Service, device and browser information, IP address, and log data.
- **Communications:** content of support requests, feedback, and any files or images you upload (e.g. in tickets).

---

## 2. How we collect personal information

We collect information when you:

- Create an account or sign in
- Use the Service (including making or receiving calls)
- Contact support or submit feedback
- Make payments or manage your subscription

We may also receive information from third-party services (e.g. Stripe for payments, telephony providers) where necessary to provide the Service.

---

## 3. Purposes for which we use personal information

We use your personal information to:

- Provide, operate, and improve the Service
- Process and manage calls and related data
- Process payments and manage your account and credits
- Respond to support requests and communicate with you
- Send service-related notices (e.g. account or security updates)
- Comply with legal obligations and protect our rights
- Analyse and improve our products and security (in a de-identified way where possible)

We will not use your personal information for purposes unrelated to these without your consent, except where permitted by law.

---

## 4. Disclosure of personal information

We may disclose your personal information to:

- **Service providers** who assist us (e.g. cloud hosting, payment processing, telephony). We require them to handle your information in accordance with this policy and Australian privacy law.
- **Professional advisers** (e.g. lawyers, accountants) where necessary.
- **Regulators or law enforcement** when required or permitted by law (e.g. court order, statutory obligation).

We do not sell your personal information to third parties.

---

## 5. Overseas disclosure

Some of our service providers (e.g. cloud or telephony providers) may be located overseas. Where we disclose personal information overseas, we take reasonable steps to ensure that the recipient complies with privacy standards that are substantially similar to the APPs, or we otherwise comply with APP 8 (e.g. consent or other permitted exception).

---

## 6. Storage and security

We store personal information in Australia and/or in secure overseas locations used by our service providers. We use industry-standard measures to protect your information, including:

- Encryption of data in transit (e.g. HTTPS)
- Secure storage and access controls
- Password hashing (we do not store plain-text passwords)
- Access limited to personnel who need it to provide the Service

While we take reasonable steps to protect your information, no method of transmission or storage is completely secure.

---

## 7. Data retention

We retain your personal information for as long as your account is active and as needed to provide the Service, comply with law, resolve disputes, and enforce our agreements. Call and transcript data are retained in accordance with our internal retention policy. You may request deletion of your personal information subject to our legal and operational requirements (see "Access and correction" below).

---

## 8. Access and correction

Under the Privacy Act, you have the right to request access to the personal information we hold about you and to request correction if it is inaccurate, out of date, or incomplete. To do so, contact us at support@voidinc.ai. We will respond within a reasonable time and may need to verify your identity. In some cases, access may be refused where the law permits (e.g. where it would impact another person's privacy).

---

## 9. Complaints

If you believe we have breached the APPs or mishandled your personal information, you may make a complaint to us at support@voidinc.ai. We will acknowledge your complaint and respond within a reasonable time. If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC): [https://www.oaic.gov.au](https://www.oaic.gov.au).

---

## 10. Notifiable Data Breaches

If we become aware of a data breach that is likely to result in serious harm to you, we will assess and, where required, notify you and the OAIC in accordance with the *Privacy Act 1988* (Notifiable Data Breaches scheme).

---

## 11. Cookies and similar technologies

We may use cookies and similar technologies for authentication, security, and to understand how the Service is used. You can adjust your browser settings to manage cookies; some features may not work correctly if you disable them.

---

## 12. Children

The Service is not intended for individuals under 18. We do not knowingly collect personal information from anyone under 18. If you become aware that a minor has provided us with personal information, please contact us so we can delete it.

---

## 13. Changes to this policy

We may update this Privacy Policy from time to time. We will post the updated policy on voidinc.ai and update the "Last updated" date. If changes are material, we may notify you by email or through the Service. Your continued use of the Service after the changes take effect constitutes acceptance of the updated policy.

---

## 14. Contact us

For privacy-related questions, access/correction requests, or complaints:

**Void Inc.**  
24 Sugars Road, Bellbowrie QLD, Australia  
Email: support@voidinc.ai
`;
